import React from 'react';

interface DimensionExceededDiagramProps {
    cargoWidth: number;
    cargoLength: number;
    cargoHeight: number;
    unitWidth: number;
    unitLength: number;
    unitHeight: number;
    unitName: string;
    exceededDims: ('width' | 'length' | 'height')[];
}

const DimensionExceededDiagram: React.FC<DimensionExceededDiagramProps> = ({
    cargoWidth, cargoLength, cargoHeight,
    unitWidth, unitLength, unitHeight,
    unitName,
    exceededDims,
}) => {
    // SVG canvas
    const svgW = 420;
    const svgH = 200;
    const pad = 40;

    // Use the 2D top-down view: X = length, Y = width
    const maxLen = Math.max(cargoLength, unitLength);
    const maxWid = Math.max(cargoWidth, unitWidth);

    const drawW = svgW - pad * 2;   // available drawing width
    const drawH = svgH - pad * 2;   // available drawing height
    const scale = Math.min(drawW / maxLen, drawH / maxWid) * 0.85;

    // Center offsets
    const originX = (svgW - maxLen * scale) / 2;
    const originY = (svgH - maxWid * scale) / 2;

    // Unit rectangle (centered)
    const uX = originX + (maxLen - unitLength) * scale / 2;
    const uY = originY + (maxWid - unitWidth) * scale / 2;
    const uW = unitLength * scale;
    const uH = unitWidth * scale;

    // Cargo rectangle (centered same origin)
    const cX = originX + (maxLen - cargoLength) * scale / 2;
    const cY = originY + (maxWid - cargoWidth) * scale / 2;
    const cW = cargoLength * scale;
    const cH = cargoWidth * scale;

    const widthExceeded = exceededDims.includes('width');
    const lengthExceeded = exceededDims.includes('length');
    const heightExceeded = exceededDims.includes('height');

    // Arrow markers
    const arrowSize = 6;

    return (
        <div className="flex flex-col items-center gap-2">
            <svg
                viewBox={`0 0 ${svgW} ${svgH}`}
                className="w-full max-w-[420px] h-auto"
                style={{ minHeight: 160 }}
            >
                <defs>
                    {/* Hatching pattern for exceeded area */}
                    <pattern id="hatch-red" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                        <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(239,68,68,0.35)" strokeWidth="1.5" />
                    </pattern>
                    {/* Arrow markers */}
                    <marker id="arrow-red-start" markerWidth={arrowSize} markerHeight={arrowSize} refX={arrowSize} refY={arrowSize / 2} orient="auto">
                        <path d={`M${arrowSize},0 L0,${arrowSize / 2} L${arrowSize},${arrowSize}`} fill="none" stroke="#ef4444" strokeWidth="1.2" />
                    </marker>
                    <marker id="arrow-red-end" markerWidth={arrowSize} markerHeight={arrowSize} refX="0" refY={arrowSize / 2} orient="auto">
                        <path d={`M0,0 L${arrowSize},${arrowSize / 2} L0,${arrowSize}`} fill="none" stroke="#ef4444" strokeWidth="1.2" />
                    </marker>
                    <marker id="arrow-gray-start" markerWidth={arrowSize} markerHeight={arrowSize} refX={arrowSize} refY={arrowSize / 2} orient="auto">
                        <path d={`M${arrowSize},0 L0,${arrowSize / 2} L${arrowSize},${arrowSize}`} fill="none" stroke="#94a3b8" strokeWidth="1" />
                    </marker>
                    <marker id="arrow-gray-end" markerWidth={arrowSize} markerHeight={arrowSize} refX="0" refY={arrowSize / 2} orient="auto">
                        <path d={`M0,0 L${arrowSize},${arrowSize / 2} L0,${arrowSize}`} fill="none" stroke="#94a3b8" strokeWidth="1" />
                    </marker>
                </defs>

                {/* Transport unit rectangle */}
                <rect
                    x={uX} y={uY} width={uW} height={uH}
                    fill="rgba(59,130,246,0.08)"
                    stroke="#3b82f6"
                    strokeWidth="2.5"
                    strokeDasharray="8 4"
                    rx="3"
                />
                {/* Unit label */}
                <text x={uX + uW / 2} y={uY - 6} textAnchor="middle" fontSize="11" fill="#64748b" fontWeight="500" fontFamily="system-ui">
                    {unitName}
                </text>

                {/* Cargo rectangle */}
                <rect
                    x={cX} y={cY} width={cW} height={cH}
                    fill="rgba(249,115,22,0.12)"
                    stroke="#f97316"
                    strokeWidth="2"
                    rx="2"
                >
                    {/* Subtle pulse animation */}
                    <animate attributeName="stroke-opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
                </rect>
                {/* Cargo label */}
                <text x={cX + cW / 2} y={cY + cH / 2 + 3} textAnchor="middle" fontSize="11" fill="#ea580c" fontWeight="600" fontFamily="system-ui">
                    Cargo
                </text>

                {/* Exceeded areas — hatched overlay for overflowing parts */}
                {/* Width overflow (top side) */}
                {widthExceeded && cY < uY && (
                    <rect x={Math.max(cX, uX)} y={cY} width={Math.min(cW, uW)} height={uY - cY} fill="url(#hatch-red)" stroke="#ef4444" strokeWidth="0.8" strokeDasharray="3 2" rx="1">
                        <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
                    </rect>
                )}
                {/* Width overflow (bottom side) */}
                {widthExceeded && (cY + cH) > (uY + uH) && (
                    <rect x={Math.max(cX, uX)} y={uY + uH} width={Math.min(cW, uW)} height={(cY + cH) - (uY + uH)} fill="url(#hatch-red)" stroke="#ef4444" strokeWidth="0.8" strokeDasharray="3 2" rx="1">
                        <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
                    </rect>
                )}
                {/* Length overflow (left side) */}
                {lengthExceeded && cX < uX && (
                    <rect x={cX} y={Math.max(cY, uY)} width={uX - cX} height={Math.min(cH, uH)} fill="url(#hatch-red)" stroke="#ef4444" strokeWidth="0.8" strokeDasharray="3 2" rx="1">
                        <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
                    </rect>
                )}
                {/* Length overflow (right side) */}
                {lengthExceeded && (cX + cW) > (uX + uW) && (
                    <rect x={uX + uW} y={Math.max(cY, uY)} width={(cX + cW) - (uX + uW)} height={Math.min(cH, uH)} fill="url(#hatch-red)" stroke="#ef4444" strokeWidth="0.8" strokeDasharray="3 2" rx="1">
                        <animate attributeName="opacity" values="0.6;1;0.6" dur="1.5s" repeatCount="indefinite" />
                    </rect>
                )}

                {/* Dimension annotation: cargo length (bottom) */}
                {lengthExceeded && (
                    <g>
                        <line x1={cX} y1={svgH - 14} x2={cX + cW} y2={svgH - 14} stroke="#ef4444" strokeWidth="1.2" markerStart="url(#arrow-red-start)" markerEnd="url(#arrow-red-end)" />
                        <text x={cX + cW / 2} y={svgH - 4} textAnchor="middle" fontSize="10" fill="#ef4444" fontWeight="700" fontFamily="system-ui">
                            {cargoLength}m
                        </text>
                    </g>
                )}
                {!lengthExceeded && (
                    <g>
                        <line x1={cX} y1={svgH - 14} x2={cX + cW} y2={svgH - 14} stroke="#94a3b8" strokeWidth="0.8" markerStart="url(#arrow-gray-start)" markerEnd="url(#arrow-gray-end)" />
                        <text x={cX + cW / 2} y={svgH - 4} textAnchor="middle" fontSize="10" fill="#94a3b8" fontWeight="500" fontFamily="system-ui">
                            {cargoLength}m
                        </text>
                    </g>
                )}

                {/* Dimension annotation: cargo width (right side) */}
                {widthExceeded && (
                    <g>
                        <line x1={svgW - 14} y1={cY} x2={svgW - 14} y2={cY + cH} stroke="#ef4444" strokeWidth="1.2" markerStart="url(#arrow-red-start)" markerEnd="url(#arrow-red-end)" />
                        <text x={svgW - 6} y={cY + cH / 2 + 3} textAnchor="middle" fontSize="10" fill="#ef4444" fontWeight="700" fontFamily="system-ui" transform={`rotate(-90, ${svgW - 6}, ${cY + cH / 2})`}>
                            {cargoWidth}m
                        </text>
                    </g>
                )}
                {!widthExceeded && (
                    <g>
                        <line x1={svgW - 14} y1={cY} x2={svgW - 14} y2={cY + cH} stroke="#94a3b8" strokeWidth="0.8" markerStart="url(#arrow-gray-start)" markerEnd="url(#arrow-gray-end)" />
                        <text x={svgW - 6} y={cY + cH / 2 + 3} textAnchor="middle" fontSize="10" fill="#94a3b8" fontWeight="500" fontFamily="system-ui" transform={`rotate(-90, ${svgW - 6}, ${cY + cH / 2})`}>
                            {cargoWidth}m
                        </text>
                    </g>
                )}

                {/* Unit dimension annotations (lighter, smaller) */}
                <line x1={uX} y1={12} x2={uX + uW} y2={12} stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="4 3" markerStart="url(#arrow-gray-start)" markerEnd="url(#arrow-gray-end)" />
                <text x={uX + uW / 2} y={8} textAnchor="middle" fontSize="10" fill="#3b82f6" fontWeight="600" fontFamily="system-ui">
                    {unitLength}m
                </text>

                <line x1={12} y1={uY} x2={12} y2={uY + uH} stroke="#60a5fa" strokeWidth="1.2" strokeDasharray="4 3" markerStart="url(#arrow-gray-start)" markerEnd="url(#arrow-gray-end)" />
                <text x={6} y={uY + uH / 2 + 3} textAnchor="middle" fontSize="10" fill="#3b82f6" fontWeight="600" fontFamily="system-ui" transform={`rotate(-90, 6, ${uY + uH / 2})`}>
                    {unitWidth}m
                </text>
            </svg>

            {/* Height warning (can't show in 2D top-down) */}
            {heightExceeded && (
                <div className="flex items-center gap-1.5 text-[13px] text-red-500 dark:text-red-400 font-medium animate-pulse">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                    </svg>
                    Cargo height ({cargoHeight}m) exceeds unit height ({unitHeight}m)
                </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-4 text-[12px] text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-2 rounded-sm border border-dashed border-blue-400 bg-blue-400/10" /> Unit
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-2 rounded-sm border-2 border-orange-500 bg-orange-500/10" /> Cargo
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-3 h-2 rounded-sm bg-red-500/30" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(239,68,68,0.3) 2px, rgba(239,68,68,0.3) 3px)' }} /> Exceeded
                </span>
            </div>
        </div>
    );
};

export default DimensionExceededDiagram;
